import React from 'react';
import { AudioPlayer } from '../components/AudioPlayer.jsx';
import { qfetch, quranDataUrl, authHeaders } from '../utils/http.js';

function pad3(n) { return String(n).padStart(3, '0'); }
function finiteDuration(a){
  try {
    const d = a.duration;
    if (Number.isFinite(d) && d > 0) return d;
    if (a.seekable && a.seekable.length) {
      const end = a.seekable.end(a.seekable.length - 1);
      if (Number.isFinite(end) && end > 0) return end;
    }
  } catch {}
  return 0;
}

const AudioCtx = React.createContext(null);
export function useAudio() { return React.useContext(AudioCtx); }

export const RECITERS = {
  //Чтецы с полной подсветкой слов 
  'Mishari_Rashid_al-Afasy': { name: 'Mishari Rashid al-Afasy', wordHighlight: true },
  'Abdul_Basit_Abdul_Samad': { name: 'Abdul Basit Abdul Samad', wordHighlight: true },
  'Abdul_Basit_Murattal': { name: 'Abdul Basit (Murattal)', wordHighlight: true },
  'Abdur-Rahman_as-Sudais': { name: 'Abdur-Rahman as-Sudais', wordHighlight: true },
  'Abu_Bakr_al-Shatri': { name: 'Abu Bakr al-Shatri', wordHighlight: true },
  'Hani_ar-Rifai': { name: 'Hani ar-Rifai', wordHighlight: true },
  'Mahmoud_Khalil_Al-Husary': { name: 'Mahmoud Khalil Al-Husary', wordHighlight: true },
  'Mahmoud_Khalil_Al-Husary_Muallim': { name: 'Mahmoud Khalil Al-Husary (Muallim)', wordHighlight: true },
  'Mohamed_Siddiq_al-Minshawi': { name: 'Mohamed Siddiq al-Minshawi', wordHighlight: true },
  'Mohamed_Siddiq_al-Minshawi_Mujawwad': { name: 'Mohamed Siddiq al-Minshawi (Mujawwad)', wordHighlight: true },
  'Mohamed_al-Tablawi': { name: 'Mohamed al-Tablawi', wordHighlight: true },
  'Saood_ash-Shuraym': { name: 'Saood ash-Shuraym', wordHighlight: true },

  //Чтецы без подсветки слов
  'Abdullaah_3awwaad_Al-Juhaynee_128kbps': { name: 'Abdullaah Al-Juhaynee', wordHighlight: false },
  'Abdullah_Matroud_128kbps': { name: 'Abdullah Matroud', wordHighlight: false },
  'Ahmed_Neana_128kbps': { name: 'Ahmed Neana', wordHighlight: false },
  'Akram_AlAlaqimy_128kbps': { name: 'Akram AlAlaqimy', wordHighlight: false },
  'Ali_Hajjaj_AlSuesy_128kbps': { name: 'Ali Hajjaj AlSuesy', wordHighlight: false },
  'Aziz_Alili_128kbps': { name: 'Aziz Alili', wordHighlight: false },
  'Hudhaify_128kbps': { name: 'Ali Abdur-Rahman al-Huthaify', wordHighlight: false },
  'Khaalid_Abdullaah_al-Qahtaanee_192kbps': { name: 'Khaalid al-Qahtaanee', wordHighlight: false },
  'Muhammad_Ayyoub_128kbps': { name: 'Muhammad Ayyoub', wordHighlight: false },
  'Muhsin_Al_Qasim_192kbps': { name: 'Muhsin Al-Qasim', wordHighlight: false },
  'Nasser_Alqatami_128kbps': { name: 'Nasser Alqatami', wordHighlight: false },
  'Sahl_Yassin_128kbps': { name: 'Sahl Yassin', wordHighlight: false },
  'Salaah_AbdulRahman_Bukhatir_128kbps': { name: 'Salaah Bukhatir', wordHighlight: false },
  'Salah_Al_Budair_128kbps': { name: 'Salah Al Budair', wordHighlight: false },
  'Yasser_Ad-Dussary_128kbps': { name: 'Yasser Ad-Dussary', wordHighlight: false },
  'ahmed_ibn_ali_al_ajamy_128kbps': { name: 'Ahmed ibn Ali al-Ajamy', wordHighlight: false },
  'MaherAlMuaiqly128kbps': { name: 'Maher al-Muaiqly', wordHighlight: false },
  'Ghamadi_40kbps': { name: 'Saad al-Ghamdi', wordHighlight: false },
  'Ali_Jaber_64kbps': { name: 'Ali Jaber', wordHighlight: false },
  'Fares_Abbad_64kbps': { name: 'Fares Abbad', wordHighlight: false },
  'Abdullah_Basfar_192kbps': { name: 'Abdullah Basfar', wordHighlight: false }
};

export function AudioProvider({ children }) {
  const audioRef = React.useRef(null);
  const [track, setTrack] = React.useState(null);
  const [isPlaying, setIsPlaying] = React.useState(false);
  const [isBuffering, setIsBuffering] = React.useState(false);
  const [currentTime, setCurrentTime] = React.useState(0);
  const [duration, setDuration] = React.useState(0);
  const durationRef = React.useRef(0);
  const durationCacheRef = React.useRef(new Map());
  const maxObservedTimeRef = React.useRef(0);
  const [playbackRate, setPlaybackRate] = React.useState(1);
  const [volume, setVolume] = React.useState(1);
  const playbackRateRef = React.useRef(1);
  const volumeRef = React.useRef(1);
  const [reciter, setReciter] = React.useState(() => {
    return localStorage.getItem('selectedReciter') || 'Mishari_Rashid_al-Afasy';
  });
  const reciterRef = React.useRef(reciter);
  const [playlist, setPlaylist] = React.useState(null);
  const objectUrlCacheRef = React.useRef(new Map());
  const lastObjectUrlRef = React.useRef(null);
  const lastObjectUrlKeyRef = React.useRef(null);
  const playRequestIdRef = React.useRef(0);
  const pendingPlayIdRef = React.useRef(0);

  React.useEffect(() => {
    reciterRef.current = reciter;
    localStorage.setItem('selectedReciter', reciter);
  }, [reciter]);

  React.useEffect(() => { playbackRateRef.current = playbackRate; }, [playbackRate]);
  React.useEffect(() => { volumeRef.current = volume; }, [volume]);

  React.useEffect(() => { durationRef.current = duration; }, [duration]);


  React.useEffect(() => {
    const a = audioRef.current;
    if (!a) return;
    const onTime = () => {
      if (pendingPlayIdRef.current) return;
      const t = a.currentTime; if (Number.isFinite(t)) setCurrentTime(t);
      if (Number.isFinite(t) && t > (maxObservedTimeRef.current || 0)) maxObservedTimeRef.current = t;
      const d = finiteDuration(a); if (d > 0 && Math.abs((durationRef.current || 0) - d) > 0.05) {
        setDuration(d);
      }
      if ((!durationRef.current || durationRef.current === 0) && (maxObservedTimeRef.current || 0) > 0) {
        setDuration(maxObservedTimeRef.current);
      }
    };
    const onMeta = () => {
      if (pendingPlayIdRef.current) return;
      const d = finiteDuration(a); if (d > 0) {
        setDuration(d);
        const tr = track; if (tr) durationCacheRef.current.set(`${reciterRef.current}:${tr.surahId}:${tr.ayah}`, d);
      }
    };
    const onLoadedData = () => {
      if (pendingPlayIdRef.current) return;
      const d = finiteDuration(a); if (d > 0) {
        setDuration(d);
        const tr = track; if (tr) durationCacheRef.current.set(`${reciterRef.current}:${tr.surahId}:${tr.ayah}`, d);
      }
    };
    const onPlay = () => {
      setIsPlaying(true);
      setIsBuffering(false);
      pendingPlayIdRef.current = 0;
      const d = finiteDuration(a);
      if (d > 0) {
        setDuration(d);
        const tr = track; if (tr) durationCacheRef.current.set(`${reciterRef.current}:${tr.surahId}:${tr.ayah}`, d);
      }
    };
    const onPause = () => setIsPlaying(false);
    const onWaiting = () => setIsBuffering(true);
    const onStalled = () => setIsBuffering(true);
    const onCanPlay = () => setIsBuffering(false);
    const onPlaying = () => setIsBuffering(false);
    const onEnded = () => {
      setIsPlaying(false);
      try { a.currentTime = 0; } catch {}
      setCurrentTime(0);
      const d = finiteDuration(a) || maxObservedTimeRef.current || 0;
      if (d > 0) {
        setDuration(d);
        const tr = track; if (tr) durationCacheRef.current.set(`${reciterRef.current}:${tr.surahId}:${tr.ayah}`, d);
      }
      setPlaylist(pl => {
        if (!pl) return null;

        if (pl.isMultiSurah && pl.playlist) {
          if (pl.currentIndex >= pl.playlist.length - 1) return null;
          const nextIndex = pl.currentIndex + 1;
          const next = pl.playlist[nextIndex];
          setTimeout(() => playAyahInternal(next.surahId, next.ayah), 100);
          return { ...pl, currentIndex: nextIndex };
        }

        if (pl.ayahs && pl.surahId) {
          if (pl.currentIndex >= pl.ayahs.length - 1) return null;
          const nextIndex = pl.currentIndex + 1;
          const nextAyah = pl.ayahs[nextIndex];
          setTimeout(() => playAyahInternal(pl.surahId, nextAyah), 100);
          return { ...pl, currentIndex: nextIndex };
        }

        return null;
      });
      try {
        document.querySelectorAll('.ayah-card').forEach(el => el.classList.remove('ayah-active'));
        document.querySelectorAll('[data-ayah] [data-widx]').forEach(el => el.classList.remove('word-active'));
        document.querySelectorAll('.quran-line').forEach(el => el.classList.remove('ayah-text-highlight'));
        document.querySelectorAll('.reading-ayah').forEach(el => el.classList.remove('ayah-text-highlight'));
      } catch {}
    };
    a.addEventListener('timeupdate', onTime);
    a.addEventListener('loadedmetadata', onMeta);
    a.addEventListener('loadeddata', onLoadedData);
    a.addEventListener('durationchange', onMeta);
    a.addEventListener('play', onPlay);
    a.addEventListener('pause', onPause);
    a.addEventListener('waiting', onWaiting);
    a.addEventListener('stalled', onStalled);
    a.addEventListener('canplay', onCanPlay);
    a.addEventListener('playing', onPlaying);
    a.addEventListener('ended', onEnded);
    return () => {
      a.removeEventListener('timeupdate', onTime);
      a.removeEventListener('loadedmetadata', onMeta);
      a.removeEventListener('loadeddata', onLoadedData);
      a.removeEventListener('durationchange', onMeta);
      a.removeEventListener('play', onPlay);
      a.removeEventListener('pause', onPause);
      a.removeEventListener('waiting', onWaiting);
      a.removeEventListener('stalled', onStalled);
      a.removeEventListener('canplay', onCanPlay);
      a.removeEventListener('playing', onPlaying);
      a.removeEventListener('ended', onEnded);
    };
  }, []);

  React.useEffect(() => {
    const a = audioRef.current; if (!a) return; let id = null;
    if (isPlaying) { id = setInterval(() => { const t = a.currentTime; if (Number.isFinite(t)) setCurrentTime(t); }, 100); }
    return () => { if (id) clearInterval(id); };
  }, [isPlaying]);

  const getAudioObjectUrl = React.useCallback(async (reciterId, sId, aNum) => {
    const key = `${reciterId}:${sId}:${aNum}`;
    const cached = objectUrlCacheRef.current.get(key);
    if (cached) return cached;
    const base = `audio/${reciterId}/surah_${pad3(sId)}/verse_${pad3(aNum)}.mp3`;
    const res = await fetch(quranDataUrl(base), { headers: authHeaders(), cache: 'no-store' });
    if (!res.ok) throw new Error('audio_fetch_failed');
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    objectUrlCacheRef.current.set(key, url);
    return url;
  }, []);

  const revokeLastObjectUrl = React.useCallback(() => {
    const u = lastObjectUrlRef.current;
    if (u) {
      try { URL.revokeObjectURL(u); } catch {}
      lastObjectUrlRef.current = null;
    }
  }, []);

  const beginBufferingAndCancelPending = React.useCallback(() => {
    const a = audioRef.current;
    const requestId = ++playRequestIdRef.current;
    pendingPlayIdRef.current = requestId;
    setIsBuffering(true);
    try { a?.pause(); } catch {}
    setIsPlaying(false);
    return requestId;
  }, []);

  const playAyahInternal = async (surahId, ayah) => {
    const a = audioRef.current; if (!a) return;
    const currentReciter = reciterRef.current;
    const requestId = ++playRequestIdRef.current;
    pendingPlayIdRef.current = requestId;
    setIsBuffering(true);
    try { a.pause(); } catch {}
    setIsPlaying(false);
    try {
      const nextKey = `${currentReciter}:${surahId}:${ayah}`;
      const src = await getAudioObjectUrl(currentReciter, surahId, ayah);
      if (requestId !== playRequestIdRef.current) return;
      if (lastObjectUrlRef.current && lastObjectUrlRef.current !== src) {
        const oldKey = lastObjectUrlKeyRef.current;
        if (oldKey) {
          try { objectUrlCacheRef.current.delete(oldKey); } catch {}
        }
        try { URL.revokeObjectURL(lastObjectUrlRef.current); } catch {}
        lastObjectUrlRef.current = null;
        lastObjectUrlKeyRef.current = null;
      }
      a.src = src;
      lastObjectUrlRef.current = src;
      lastObjectUrlKeyRef.current = nextKey;
    } catch (e) {
      console.error('Failed to load audio:', e);
      if (requestId === playRequestIdRef.current) {
        pendingPlayIdRef.current = 0;
        setIsBuffering(false);
      }
      return;
    }
    try { a.load(); } catch {}
    try { a.currentTime = 0; } catch {}
    const cacheKey = `${currentReciter}:${surahId}:${ayah}`;
    const cached = durationCacheRef.current.get(cacheKey);

    const onLoadedMetadata = () => {
      const d = finiteDuration(a); if (d > 0) { durationCacheRef.current.set(`${currentReciter}:${surahId}:${ayah}`, d); }
      a.playbackRate = playbackRateRef.current;
      a.volume = volumeRef.current;
      a.removeEventListener('loadedmetadata', onLoadedMetadata);
    };
    a.addEventListener('loadedmetadata', onLoadedMetadata);
    const onLoadedData2 = () => { const d = finiteDuration(a); if (d > 0) { durationCacheRef.current.set(`${currentReciter}:${surahId}:${ayah}`, d); } a.removeEventListener('loadeddata', onLoadedData2); };
    a.addEventListener('loadeddata', onLoadedData2);

    a.playbackRate = playbackRateRef.current;
    a.volume = volumeRef.current;

    const commit = () => {
      if (requestId !== playRequestIdRef.current) return;
      setTrack({ surahId, ayah });
      setCurrentTime(0);
      maxObservedTimeRef.current = 0;
      setDuration((durationCacheRef.current.get(cacheKey) || cached) || 0);
      pendingPlayIdRef.current = 0;
      setIsBuffering(false);
    };

    a.play().then(() => {
      commit();
      setIsPlaying(true);
    }).catch(() => {
      const onReady = () => {
        a.removeEventListener('loadeddata', onReady);
        if (requestId !== playRequestIdRef.current) return;
        a.play().then(() => {
          commit();
          setIsPlaying(true);
        }).catch(() => {
          if (requestId === playRequestIdRef.current) pendingPlayIdRef.current = 0;
          if (requestId === playRequestIdRef.current) setIsBuffering(false);
          setIsPlaying(false);
        });
      };
      a.addEventListener('loadeddata', onReady);
    });
  };

  const playAyah = (surahId, ayah) => {
    const a = audioRef.current; if (!a) return;
    if (track && track.surahId === surahId && track.ayah === ayah && !a.paused) { a.pause(); return; }
    setPlaylist(null);
    playAyahInternal(surahId, ayah);
  };

  const playSurah = async (surahId) => {
    const startId = beginBufferingAndCancelPending();
    try {
      const response = await qfetch(`text_surah/surah_${pad3(surahId)}_complete.json`);
      const data = await response.json();
      const ayahCount = data?.alquran_translations?.numberOfAyahs || 0;
      if (ayahCount === 0) {
        if (startId === playRequestIdRef.current) {
          pendingPlayIdRef.current = 0;
          setIsBuffering(false);
        }
        return;
      }

      const ayahs = Array.from({ length: ayahCount }, (_, i) => i + 1);
      setPlaylist({ surahId, ayahs, currentIndex: 0 });
      playAyahInternal(surahId, 1);
    } catch (err) {
      console.error('Failed to load surah data:', err);
      if (startId === playRequestIdRef.current) {
        pendingPlayIdRef.current = 0;
        setIsBuffering(false);
      }
    }
  };

  const playJuz = async (juzId) => {
    const startId = beginBufferingAndCancelPending();
    try {
      const response = await qfetch('index/juz_index.json');
      const juzIndex = await response.json();
      const bucket = juzIndex[String(juzId)] || juzIndex[juzId] || [];
      if (bucket.length === 0) {
        if (startId === playRequestIdRef.current) {
          pendingPlayIdRef.current = 0;
          setIsBuffering(false);
        }
        return;
      }

      const playlist = [];
      for (const item of bucket) {
        const ayahs = item.ayahs || [];
        for (const ayah of ayahs) {
          playlist.push({ surahId: item.surah, ayah });
        }
      }

      if (playlist.length === 0) {
        if (startId === playRequestIdRef.current) {
          pendingPlayIdRef.current = 0;
          setIsBuffering(false);
        }
        return;
      }
      setPlaylist({ playlist, currentIndex: 0, isMultiSurah: true });
      playAyahInternal(playlist[0].surahId, playlist[0].ayah);
    } catch (err) {
      console.error('Failed to load juz data:', err);
      if (startId === playRequestIdRef.current) {
        pendingPlayIdRef.current = 0;
        setIsBuffering(false);
      }
    }
  };

  const playHizb = async (hizbId) => {
    const startId = beginBufferingAndCancelPending();
    try {
      const response = await qfetch('index/hizb_index.json');
      const hizbIndex = await response.json();
      const bucket = hizbIndex[String(hizbId)] || hizbIndex[hizbId] || [];
      if (bucket.length === 0) {
        if (startId === playRequestIdRef.current) {
          pendingPlayIdRef.current = 0;
          setIsBuffering(false);
        }
        return;
      }

      const playlist = [];
      for (const item of bucket) {
        const ayahs = item.ayahs || [];
        for (const ayah of ayahs) {
          playlist.push({ surahId: item.surah, ayah });
        }
      }

      if (playlist.length === 0) {
        if (startId === playRequestIdRef.current) {
          pendingPlayIdRef.current = 0;
          setIsBuffering(false);
        }
        return;
      }
      setPlaylist({ playlist, currentIndex: 0, isMultiSurah: true });
      playAyahInternal(playlist[0].surahId, playlist[0].ayah);
    } catch (err) {
      console.error('Failed to load hizb data:', err);
      if (startId === playRequestIdRef.current) {
        pendingPlayIdRef.current = 0;
        setIsBuffering(false);
      }
    }
  };

  const playPage = async (pageId) => {
    const startId = beginBufferingAndCancelPending();
    try {
      const response = await qfetch('index/page_index.json');
      const pageIndex = await response.json();
      const bucket = pageIndex[String(pageId)] || pageIndex[pageId] || [];
      if (bucket.length === 0) {
        if (startId === playRequestIdRef.current) {
          pendingPlayIdRef.current = 0;
          setIsBuffering(false);
        }
        return;
      }

      const playlist = [];
      for (const item of bucket) {
        const ayahs = item.ayahs || [];
        for (const ayah of ayahs) {
          playlist.push({ surahId: item.surah, ayah });
        }
      }

      if (playlist.length === 0) {
        if (startId === playRequestIdRef.current) {
          pendingPlayIdRef.current = 0;
          setIsBuffering(false);
        }
        return;
      }
      setPlaylist({ playlist, currentIndex: 0, isMultiSurah: true });
      playAyahInternal(playlist[0].surahId, playlist[0].ayah);
    } catch (err) {
      console.error('Failed to load page data:', err);
      if (startId === playRequestIdRef.current) {
        pendingPlayIdRef.current = 0;
        setIsBuffering(false);
      }
    }
  };

  const togglePlayPause = () => {
    const a = audioRef.current; if (!a) return;
    if (a.paused) {
      if (a.ended) {
        if (track) {
          playAyahInternal(track.surahId, track.ayah);
          return;
        } else {
          try { a.currentTime = 0; } catch {}
          setCurrentTime(0);
          try { a.load(); } catch {}
          try { a.playbackRate = playbackRate; a.volume = volume; } catch {}
        }
      }
      const d = a.duration; if (Number.isFinite(d)) setDuration(d);
      a.play().then(() => setIsPlaying(true)).catch(() => setIsPlaying(false));
    } else { a.pause(); setIsPlaying(false); }
  };
  const seek = (time) => { const a = audioRef.current; if (!a) return; a.currentTime = time; setCurrentTime(time); };
  const close = () => {
    const a = audioRef.current; if (!a) return;
    const tr = track;
    const oldReciter = reciterRef.current;
    a.pause();
    a.currentTime = 0;
    setTrack(null);
    setPlaylist(null);
    setIsPlaying(false);
    setCurrentTime(0);
    setDuration(0);
    if (tr) {
      const key = `${oldReciter}:${tr.surahId}:${tr.ayah}`;
      try { objectUrlCacheRef.current.delete(key); } catch {}
    }
    if (lastObjectUrlRef.current) {
      const oldKey = lastObjectUrlKeyRef.current;
      if (oldKey) {
        try { objectUrlCacheRef.current.delete(oldKey); } catch {}
      }
      try { URL.revokeObjectURL(lastObjectUrlRef.current); } catch {}
      lastObjectUrlRef.current = null;
      lastObjectUrlKeyRef.current = null;
    }
    document.querySelectorAll('.ayah-card').forEach(el => el.classList.remove('ayah-active'));
    document.querySelectorAll('[data-ayah] [data-widx]').forEach(el => el.classList.remove('word-active'));
    document.querySelectorAll('.quran-line').forEach(el => el.classList.remove('ayah-text-highlight'));
    document.querySelectorAll('.reading-ayah').forEach(el => el.classList.remove('ayah-text-highlight'));
  };
  const changeRate = (rate) => { setPlaybackRate(rate); const a = audioRef.current; if (a) a.playbackRate = rate; };
  const changeVolume = (vol) => { setVolume(vol); const a = audioRef.current; if (a) a.volume = vol; };
  const changeReciter = (newReciter) => {
    const startId = track ? beginBufferingAndCancelPending() : 0;
    const prevReciter = reciterRef.current;
    setReciter(newReciter);
    if (track) {
      const a = audioRef.current;
      if (!a) return;

      const savedTime = a.currentTime || 0;
      const wasPlaying = !a.paused;

      a.pause();
      (async () => {
        try {
          if (startId && startId !== playRequestIdRef.current) return;
          const src = await getAudioObjectUrl(newReciter, track.surahId, track.ayah);
          if (startId && startId !== playRequestIdRef.current) return;
          const oldKey = `${prevReciter}:${track.surahId}:${track.ayah}`;
          try { objectUrlCacheRef.current.delete(oldKey); } catch {}
          if (lastObjectUrlRef.current && lastObjectUrlRef.current !== src) {
            const prevKey = lastObjectUrlKeyRef.current;
            if (prevKey) {
              try { objectUrlCacheRef.current.delete(prevKey); } catch {}
            }
            try { URL.revokeObjectURL(lastObjectUrlRef.current); } catch {}
            lastObjectUrlRef.current = null;
            lastObjectUrlKeyRef.current = null;
          }
          a.src = src;
          lastObjectUrlRef.current = src;
          lastObjectUrlKeyRef.current = `${newReciter}:${track.surahId}:${track.ayah}`;
          const onCanPlay = () => {
            if (startId && startId !== playRequestIdRef.current) {
              a.removeEventListener('canplay', onCanPlay);
              return;
            }
            a.currentTime = savedTime;
            a.playbackRate = playbackRateRef.current;
            a.volume = volumeRef.current;
            const d = finiteDuration(a); if (d > 0) setDuration(d);
            if (wasPlaying) {
              a.play().then(() => setIsPlaying(true)).catch(() => setIsPlaying(false));
            }
            if (!wasPlaying) {
              pendingPlayIdRef.current = 0;
              setIsBuffering(false);
            }
            a.removeEventListener('canplay', onCanPlay);
          };
          a.addEventListener('canplay', onCanPlay);
          a.load();
        } catch (e) {
          console.error('Failed to switch reciter audio:', e);
          if (startId && startId === playRequestIdRef.current) {
            pendingPlayIdRef.current = 0;
            setIsBuffering(false);
          }
        }
      })();
    }
    try {
      document.querySelectorAll('[data-ayah] [data-widx]').forEach(el => el.classList.remove('word-active'));
      document.querySelectorAll('.quran-line').forEach(el => el.classList.remove('ayah-text-highlight'));
      document.querySelectorAll('.reading-ayah').forEach(el => el.classList.remove('ayah-text-highlight'));
    } catch {}
  };

  const playPrev = () => {
    if (!playlist) return;

    if (playlist.isMultiSurah && playlist.playlist) {
      if (playlist.currentIndex <= 0) return;
      const prevIndex = playlist.currentIndex - 1;
      const prev = playlist.playlist[prevIndex];
      setPlaylist({ ...playlist, currentIndex: prevIndex });
      playAyahInternal(prev.surahId, prev.ayah);
    } else if (playlist.ayahs && playlist.surahId) {
      if (playlist.currentIndex <= 0) return;
      const prevIndex = playlist.currentIndex - 1;
      const prevAyah = playlist.ayahs[prevIndex];
      setPlaylist({ ...playlist, currentIndex: prevIndex });
      playAyahInternal(playlist.surahId, prevAyah);
    }
  };

  const playNext = () => {
    if (!playlist) return;

    if (playlist.isMultiSurah && playlist.playlist) {
      if (playlist.currentIndex >= playlist.playlist.length - 1) return;
      const nextIndex = playlist.currentIndex + 1;
      const next = playlist.playlist[nextIndex];
      setPlaylist({ ...playlist, currentIndex: nextIndex });
      playAyahInternal(next.surahId, next.ayah);
    } else if (playlist.ayahs && playlist.surahId) {
      if (playlist.currentIndex >= playlist.ayahs.length - 1) return;
      const nextIndex = playlist.currentIndex + 1;
      const nextAyah = playlist.ayahs[nextIndex];
      setPlaylist({ ...playlist, currentIndex: nextIndex });
      playAyahInternal(playlist.surahId, nextAyah);
    }
  };

  const hasPrev = playlist ? playlist.currentIndex > 0 : false;
  const hasNext = playlist ?
    (playlist.isMultiSurah && playlist.playlist ?
      playlist.currentIndex < playlist.playlist.length - 1 :
      (playlist.ayahs ? playlist.currentIndex < playlist.ayahs.length - 1 : false)
    ) : false;

  const hasWordHighlight = RECITERS[reciter]?.wordHighlight !== false;

  const ctxValue = {
    audioRef,
    track, isPlaying, isBuffering, currentTime, duration,
    playbackRate, volume, reciter, playlist, hasWordHighlight,
    playAyah, playSurah, playJuz, playHizb, playPage,
    togglePlayPause, seek, close, changeRate, changeVolume, changeReciter,
    playPrev, playNext, hasPrev, hasNext
  };

  return (
    <AudioCtx.Provider value={ctxValue}>
      {children}
      <audio ref={audioRef} preload="auto" style={{ display: 'none' }} />
      {track && (
        <AudioPlayer
          src={`${quranDataUrl(`audio/${reciter}/surah_${pad3(track.surahId)}/verse_${pad3(track.ayah)}.mp3`)}`}
          isPlaying={isPlaying}
          isBuffering={isBuffering}
          currentTime={currentTime}
          duration={duration}
          onPlayPause={togglePlayPause}
          onSeek={seek}
          onClose={close}
          onDownload={() => {
            const a = document.createElement('a');
            a.href = quranDataUrl(`audio/${reciter}/surah_${pad3(track.surahId)}/verse_${pad3(track.ayah)}.mp3`);
            a.download = `surah_${pad3(track.surahId)}_ayah_${pad3(track.ayah)}.mp3`;
            a.click();
          }}
          playbackRate={playbackRate}
          onChangePlaybackRate={changeRate}
          volume={volume}
          onVolumeChange={changeVolume}
          reciter={reciter}
          availableReciters={RECITERS}
          onChangeReciter={changeReciter}
          title={`${track.surahId}:${track.ayah}`}
          subtitle={RECITERS[reciter]?.name || reciter}
          onPrev={playPrev}
          onNext={playNext}
          hasPrev={hasPrev}
          hasNext={hasNext}
        />
      )}
    </AudioCtx.Provider>
  );
}
